import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { STORAGE_THEME, STORAGE_LOCALE } from "@weco/suite-client";
import { resolveEffectiveTheme, type SuiteTheme } from "./initTheme";
import { applyDomLocale, type SuiteLocale } from "./initLocale";

type Ctx = {
  theme: SuiteTheme;
  /** Resolved light/dark for UI (e.g. topbar icon) when preference is `system`. */
  effectiveTheme: "dark" | "light";
  toggleTheme: () => void;
  setTheme: (next: SuiteTheme) => void;
  locale: SuiteLocale;
  toggleLocale: () => void;
};

const SuiteChromeContext = createContext<Ctx | null>(null);

export function useSuiteChromeLayout(): Ctx {
  const v = useContext(SuiteChromeContext);
  if (!v) throw new Error("useSuiteChromeLayout must be used within SuiteAppShell");
  return v;
}

function applyDomTheme(stored: SuiteTheme): void {
  const effective = resolveEffectiveTheme(stored);
  // Surfaces with a colour transition (the sidebar is `transition-all
  // duration-300`, nav rows and buttons fade their hover colours) would
  // otherwise animate to the new theme while everything else snaps, leaving
  // a light sidebar beside a dark page for a beat. Suspend transitions for
  // the one frame the theme flips so the whole UI switches at once.
  const freeze = document.createElement("style");
  freeze.dataset.themeSwitch = "";
  freeze.textContent = "*,*::before,*::after{transition:none!important}";
  document.head.appendChild(freeze);

  document.documentElement.dataset.theme = effective;
  if (effective === "dark") {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }

  // Force a style flush with transitions off, then restore them next frame.
  void window.getComputedStyle(document.body).backgroundColor;
  requestAnimationFrame(() => freeze.remove());
}

export const SuiteChromeProvider: React.FC<{
  initialTheme: SuiteTheme;
  initialLocale?: SuiteLocale;
  children: React.ReactNode;
}> = ({ initialTheme, initialLocale = "en", children }) => {
  const [theme, setThemeState] = useState<SuiteTheme>(initialTheme);
  const [locale, setLocaleState] = useState<SuiteLocale>(initialLocale);

  const setTheme = useCallback((next: SuiteTheme) => {
    localStorage.setItem(STORAGE_THEME, next);
    applyDomTheme(next);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const effective = resolveEffectiveTheme(prev);
      const next: SuiteTheme = effective === "dark" ? "light" : "dark";
      localStorage.setItem(STORAGE_THEME, next);
      applyDomTheme(next);
      return next;
    });
  }, []);

  const toggleLocale = useCallback(() => {
    setLocaleState((prev) => {
      const next: SuiteLocale = prev === "en" ? "ar" : "en";
      localStorage.setItem(STORAGE_LOCALE, next);
      applyDomLocale(next);
      window.dispatchEvent(new Event("weco-locale-updated"));
      return next;
    });
  }, []);

  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyDomTheme("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const effectiveTheme = resolveEffectiveTheme(theme);

  const value = useMemo(
    () => ({ theme, effectiveTheme, toggleTheme, setTheme, locale, toggleLocale }),
    [theme, effectiveTheme, toggleTheme, setTheme, locale, toggleLocale],
  );

  return <SuiteChromeContext.Provider value={value}>{children}</SuiteChromeContext.Provider>;
};
